
import React, { useState, useEffect } from 'react';
import { ref, set, push } from "firebase/database";
import { useLocation } from 'react-router-dom';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { FaMapMarkerAlt, FaCreditCard, FaLock, FaArrowLeft, FaShoppingBag } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { loadRazorpay } from './razorpay';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    zipCode: '',
    paymentMethod: 'razorpay',
  });
  const [errors, setErrors] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const items = JSON.parse(localStorage.getItem('cart')) || [];
    setCartItems(items.map(item => ({ 
      ...item, 
      price: parseFloat(item.price || 0), 
      discount: parseFloat(item.discount || 0), 
      quantity: parseInt(item.quantity || 1) 
    })));
    setLoading(false);
  }, []);

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      const itemPrice = parseFloat(item.price || 0) * (1 - parseFloat(item.discount || 0) / 100);
      return total + (itemPrice * parseInt(item.quantity || 1));
    }, 0).toFixed(2);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.country.trim()) newErrors.country = 'Country is required';
    if (!formData.zipCode.trim()) newErrors.zipCode = 'Zip code is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createOrderData = (paymentId = null) => {
    const totalAmount = parseFloat(calculateTotal());
    const orderId = `ORD-${Date.now()}`;
    const date = new Date().toISOString();

    return {
      orderId,
      date,
      items: cartItems.map(item => ({
        id: item.id,
        name: item.name,
        price: parseFloat(item.price || 0),
        discountedPrice: parseFloat(item.price || 0) * (1 - parseFloat(item.discount || 0) / 100),
        quantity: parseInt(item.quantity || 1),
        image: item.image
      })),
      total: totalAmount,
      shippingAddress: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        country: formData.country,
        zipCode: formData.zipCode
      },
      paymentMethod: {
        type: 'razorpay',
        paymentId: paymentId,
        status: paymentId ? 'completed' : 'pending'
      },
      status: paymentId ? 'Processing' : 'Pending'
    };
  };

  const saveOrderToDatabase = async (orderData) => {
    try {
      const user = JSON.parse(localStorage.getItem('userData'));

      if (user && user.id) {
        const ordersRef = ref(db, `Watch/customers/${user.id}/orders`);
        const newOrderRef = push(ordersRef);
        await set(newOrderRef, orderData);
      }

      const adminOrdersRef = ref(db, 'Watch/admin/orders');
      const adminNewOrderRef = push(adminOrdersRef);
      await set(adminNewOrderRef, {
        ...orderData,
        customerId: user?.id || 'guest',
        customerName: `${orderData.shippingAddress.firstName} ${orderData.shippingAddress.lastName}`
      });

      return true;
    } catch (error) {
      console.error('Error saving order:', error);
      return false;
    }
  };

  const processRazorpayPayment = async () => {
    try {
      if (!window.Razorpay) {
        await loadRazorpay();
      }

      const totalAmount = parseFloat(calculateTotal()) * 100;

      const orderResponse = await fetch('https://swoct.in/Watch/createOrder.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: totalAmount,
          currency: 'INR'
        })
      });

      const orderData = await orderResponse.json();

      if (!orderData.id) {
        throw new Error(orderData.error || 'Failed to create Razorpay order');
      }

      const order = createOrderData();

      const options = {
        key: orderData.key || 'process.env.REACT_APP_RAZORPAY_KEY_ID', // fetch from .env
        amount: orderData.amount,
        currency: orderData.currency,
        order_id: orderData.id,
        name: 'SURYAWANSHI WATCH & OPTICIANS',
        description: 'Purchase of watches',
        image: '/logo.png',
        handler: async function (response) {
          try {
            const verificationResponse = await fetch('https://swoct.in/Watch/razorpay-verify.php', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature
              })
            });

            const verificationData = await verificationResponse.json();

            if (!verificationData.success) {
              throw new Error(verificationData.error || 'Payment verification failed');
            }

            const completedOrder = {
              ...order,
              paymentMethod: {
                ...order.paymentMethod,
                paymentId: response.razorpay_payment_id,
                status: 'completed'
              },
              status: 'Processing'
            };

            const saveSuccess = await saveOrderToDatabase(completedOrder);

            if (!saveSuccess) {
              throw new Error('Failed to save order to database');
            }

            const localOrders = JSON.parse(localStorage.getItem('orders')) || [];
            localOrders.unshift(completedOrder);
            localStorage.setItem('orders', JSON.stringify(localOrders));

            localStorage.removeItem('cart');
            setCartItems([]);

            navigate('/order-confirmation', {
              state: { order: completedOrder }
            });

          } catch (error) {
            console.error('Payment processing error:', error);
            alert('Payment verification failed. Please contact support with your payment ID.');
          }
        },
        prefill: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          contact: formData.phone
        },
        notes: {
          address: formData.address
        },
        theme: {
          color: '#6d28d9'
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (error) {
      console.error('Razorpay error:', error);
      alert('Payment processing failed. Please try again. Error: ' + error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    await processRazorpayPayment();
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3
      }
    }
  };

  // Main container styles
  const containerStyles = {
    minHeight: '100vh',
    background: 'linear-gradient(to bottom right, #f5f3ff, #e0e7ff)',
    padding: isMobile ? '24px 16px' : '32px 16px',
    fontFamily: "'Inter', sans-serif"
  };

  // Inner container styles
  const innerContainerStyles = {
    maxWidth: '1200px',
    margin: '0 auto'
  };

  // Header styles
  const headerStyles = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: isMobile ? '24px' : '32px',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'flex-start' : 'center'
  };

  // Back button styles
  const backButtonStyles = {
    display: 'flex',
    alignItems: 'center',
    color: '#7c3aed',
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    fontSize: '16px',
    marginRight: '24px',
    marginBottom: isMobile ? '16px' : '0',
    padding: isMobile ? '8px 0' : '8px',
    transition: 'all 0.2s ease'
  };

  // Title styles
  const titleStyles = {
    fontSize: isMobile ? '24px' : '28px',
    fontWeight: '700',
    color: '#1e1b4b',
    margin: '0',
    marginBottom: isMobile ? '16px' : '0'
  };

  // Total badge styles
  const totalBadgeStyles = {
    background: 'white',
    padding: '8px 16px',
    borderRadius: '999px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginLeft: isMobile ? '0' : 'auto',
    marginTop: isMobile ? '16px' : '0',
    alignSelf: isMobile ? 'flex-start' : 'center'
  };

  // Content container styles
  const contentContainerStyles = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    gap: '24px'
  };

  // Form section styles
  const formSectionStyles = {
    flex: '2',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
    overflow: 'hidden',
    transition: 'all 0.3s ease'
  };

  // Section header styles
  const sectionHeaderStyles = {
    background: 'linear-gradient(to right, #7c3aed, #6366f1)',
    padding: isMobile ? '16px' : '24px',
    color: 'white'
  };

  // Section title styles
  const sectionTitleStyles = {
    fontSize: isMobile ? '18px' : '20px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    margin: '0'
  };

  // Section subtitle styles
  const sectionSubtitleStyles = {
    fontSize: '14px',
    color: '#c4b5fd',
    marginTop: '4px'
  };

  // Form styles
  const formStyles = {
    padding: isMobile ? '16px' : '24px'
  };

  // Grid styles
  const gridStyles = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    gap: '16px'
  };

  // Input group styles
  const inputGroupStyles = {
    marginBottom: '16px'
  };

  // Label styles
  const labelStyles = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#4b5563',
    marginBottom: '8px'
  };

  // Input styles
  const inputStyles = {
    width: isMobile ? '100%' : '90%',
    padding: '12px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    transition: 'all 0.2s ease'
  };

  // Error input styles
  const errorInputStyles = {
    ...inputStyles,
    borderColor: '#ef4444'
  };

  // Error text styles
  const errorTextStyles = {
    fontSize: '12px',
    color: '#ef4444',
    marginTop: '4px'
  };

  // Payment section styles
  const paymentSectionStyles = {
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #e5e7eb'
  };

  // Payment method styles
  const paymentMethodStyles = {
    background: '#f5f3ff',
    padding: '16px',
    borderRadius: '8px',
    marginTop: '12px'
  };

  // Radio styles
  const radioStyles = {
    marginRight: '12px',
    accentColor: '#7c3aed'
  };

  // Submit button styles
  const submitButtonStyles = {
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(to right, #7c3aed, #6366f1)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '24px',
    boxShadow: '0 4px 6px rgba(124, 58, 237, 0.2)',
    transition: 'all 0.2s ease'
  };

  // Secure note styles
  const secureNoteStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '16px'
  };

  // Summary section styles
  const summarySectionStyles = {
    flex: '1',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
    overflow: 'hidden',
    position: isMobile ? 'static' : 'sticky',
    top: '32px',
    alignSelf: 'flex-start'
  };

  // Cart item styles
  const cartItemStyles = {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: '16px',
    borderBottom: '1px solid #f3f4f6',
    marginBottom: '16px'
  };

  // Item image styles
  const itemImageStyles = {
    width: '80px',
    height: '80px',
    borderRadius: '8px',
    objectFit: 'cover',
    background: '#f3f4f6'
  };

  // Item details styles
  const itemDetailsStyles = {
    marginLeft: '16px',
    flex: '1'
  };

  // Item name styles
  const itemNameStyles = {
    fontSize: '14px',
    fontWeight: '500',
    color: '#1f2937',
    marginBottom: '4px'
  };

  // Item price styles
  const itemPriceStyles = {
    fontSize: '14px',
    color: '#7c3aed',
    fontWeight: '600'
  };

  // Original price styles
  const originalPriceStyles = {
    fontSize: '12px',
    color: '#9ca3af',
    textDecoration: 'line-through',
    marginLeft: '8px'
  };

  // Summary row styles
  const summaryRowStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '12px'
  };

  // Summary label styles
  const summaryLabelStyles = {
    fontSize: '14px',
    color: '#6b7280'
  };

  // Summary value styles
  const summaryValueStyles = {
    fontSize: '14px',
    fontWeight: '500',
    color: '#1f2937'
  };

  // Total row styles
  const totalRowStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '24px',
    paddingTop: '16px',
    borderTop: '1px solid #e5e7eb'
  };

  // Total label styles
  const totalLabelStyles = {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1f2937'
  };

  // Total value styles
  const totalValueStyles = {
    fontSize: '20px',
    fontWeight: '700',
    color: '#7c3aed'
  };

  // Loading container styles
  const loadingContainerStyles = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(to bottom right, #f5f3ff, #e0e7ff)'
  };

  // Loading content styles
  const loadingContentStyles = {
    textAlign: 'center',
    padding: '48px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
    maxWidth: '500px',
    width: '100%'
  };

  // Spinner styles
  const spinnerStyles = {
    border: '4px solid rgba(124, 58, 237, 0.1)',
    borderTop: '4px solid #7c3aed',
    borderRadius: '50%',
    width: '48px',
    height: '48px',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 16px'
  };

  // Empty cart container styles
  const emptyCartContainerStyles = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(to bottom right, #f5f3ff, #e0e7ff)'
  };

  // Empty cart content styles
  const emptyCartContentStyles = {
    textAlign: 'center',
    padding: '48px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
    maxWidth: '500px',
    width: '100%'
  };

  // Empty cart icon styles
  const emptyCartIconStyles = {
    background: '#f5f3ff',
    padding: '16px',
    borderRadius: '50%',
    display: 'inline-flex',
    marginBottom: '16px'
  };

  // Empty cart title styles
  const emptyCartTitleStyles = {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1e1b4b',
    marginBottom: '8px'
  };

  // Empty cart text styles
  const emptyCartTextStyles = {
    fontSize: '16px',
    color: '#6b7280',
    marginBottom: '24px'
  };

  // Continue shopping button styles
  const continueShoppingButtonStyles = {
    background: 'linear-gradient(to right, #7c3aed, #6366f1)',
    color: 'white',
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    boxShadow: '0 4px 6px rgba(124, 58, 237, 0.2)',
    transition: 'all 0.2s ease'
  };

  if (loading) {
    return (
      <div style={loadingContainerStyles}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          style={loadingContentStyles}
        >
          <div style={spinnerStyles}></div>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1e1b4b' }}>Preparing your checkout</h2>
          <p style={{ color: '#6b7280', marginTop: '8px' }}>Just a moment while we get everything ready...</p>
        </motion.div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div style={emptyCartContainerStyles}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          style={emptyCartContentStyles}
        >
          <div style={emptyCartIconStyles}>
            <FaShoppingBag style={{ color: '#7c3aed', fontSize: '24px' }} />
          </div>
          <h2 style={emptyCartTitleStyles}>Your cart is empty</h2>
          <p style={emptyCartTextStyles}>
            You need to add items to your cart before proceeding to checkout
          </p>
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/products')}
            style={continueShoppingButtonStyles}
          >
            Continue Shopping
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      style={containerStyles}
    >
      <div style={innerContainerStyles}>
        {/* Header */}
        <div style={headerStyles}>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate(-1)}
            style={backButtonStyles}
          >
            <FaArrowLeft style={{ marginRight: '8px' }} /> Back
          </motion.button>
          <h1 style={titleStyles}>Checkout</h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            style={totalBadgeStyles}
          >
            <span style={{ color: '#6b7280' }}>Total:</span>
            <span style={{ marginLeft: '8px', fontWeight: '600', color: '#7c3aed' }}>₹{calculateTotal()}</span>
          </motion.div>
        </div>

        <div style={contentContainerStyles}>
          {/* Left Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            style={formSectionStyles}
          >
            <div style={sectionHeaderStyles}>
              <h2 style={sectionTitleStyles}>
                <FaMapMarkerAlt style={{ marginRight: '12px' }} /> Shipping Information
              </h2>
              <p style={sectionSubtitleStyles}>Enter your details to complete the order</p>
            </div>

            <form onSubmit={handleSubmit} style={formStyles}>
              <div style={{ ...gridStyles, marginBottom: '16px' }}>
                <motion.div
                  variants={itemVariants}
                  style={inputGroupStyles}
                >
                  <label style={labelStyles}>First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    style={errors.firstName ? errorInputStyles : inputStyles}
                    placeholder="Enter your name"
                  />
                  {errors.firstName && (
                    <p style={errorTextStyles}>{errors.firstName}</p>
                  )}
                </motion.div>
                <motion.div
                  variants={itemVariants}
                  style={inputGroupStyles}
                >
                  <label style={labelStyles}>Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    style={errors.lastName ? errorInputStyles : inputStyles}
                    placeholder="Enter your last name"
                  />
                  {errors.lastName && (
                    <p style={errorTextStyles}>{errors.lastName}</p>
                  )}
                </motion.div>
              </div>

              <motion.div
                variants={itemVariants}
                style={inputGroupStyles}
              >
                <label style={labelStyles}>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  style={errors.email ? errorInputStyles : inputStyles}
                  placeholder="Enter your Email"
                />
                {errors.email && (
                  <p style={errorTextStyles}>{errors.email}</p>
                )}
              </motion.div>

              <motion.div
                variants={itemVariants}
                style={inputGroupStyles}
              >
                <label style={labelStyles}>Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  style={inputStyles}
                  placeholder="Enter your phone number"
                />
              </motion.div>

              <motion.div
                variants={itemVariants}
                style={inputGroupStyles}
              >
                <label style={labelStyles}>Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  style={errors.address ? errorInputStyles : inputStyles}
                  placeholder="Enter your address"
                />
                {errors.address && (
                  <p style={errorTextStyles}>{errors.address}</p>
                )}
              </motion.div>

              <div style={{ ...gridStyles, marginBottom: '16px' }}>
                <motion.div
                  variants={itemVariants}
                  style={inputGroupStyles}
                >
                  <label style={labelStyles}>City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    style={errors.city ? errorInputStyles : inputStyles}
                    placeholder="Enter your city"
                  />
                  {errors.city && (
                    <p style={errorTextStyles}>{errors.city}</p>
                  )}
                </motion.div>
                <motion.div
                  variants={itemVariants}
                  style={inputGroupStyles}
                >
                  <label style={labelStyles}>Country</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    style={errors.country ? errorInputStyles : inputStyles}
                    placeholder="Enter your country"
                  />
                  {errors.country && (
                    <p style={errorTextStyles}>{errors.country}</p>
                  )}
                </motion.div>
                <motion.div
                  variants={itemVariants}
                  style={inputGroupStyles}
                >
                  <label style={labelStyles}>Zip Code</label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    style={errors.zipCode ? errorInputStyles : inputStyles}
                    placeholder="Enter your zip code"
                  />
                  {errors.zipCode && (
                    <p style={errorTextStyles}>{errors.zipCode}</p>
                  )}
                </motion.div>
              </div>

              {/* Payment Method */}
              <motion.div
                variants={itemVariants}
                style={paymentSectionStyles}
              >
                <h3 style={{ ...sectionTitleStyles, color: '#1e1b4b', fontSize: '18px' }}>
                  <FaCreditCard style={{ marginRight: '12px', color: '#7c3aed' }} /> Payment Method
                </h3>
                <div style={paymentMethodStyles}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="radio"
                      id="razorpay"
                      name="paymentMethod"
                      value="razorpay"
                      checked={formData.paymentMethod === 'razorpay'}
                      onChange={handleInputChange}
                      style={radioStyles}
                    />
                    <label htmlFor="razorpay" style={{ ...labelStyles, marginBottom: '0' }}>
                      Razorpay (Credit/Debit Cards, UPI, Net Banking)
                    </label>
                  </div>
                </div>
              </motion.div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                style={submitButtonStyles}
              >
                <FaLock style={{ marginRight: '12px' }} /> Complete Secure Checkout
              </motion.button>

              <motion.div
                variants={itemVariants}
                style={secureNoteStyles}
              >
                <FaLock style={{ marginRight: '8px', color: '#7c3aed' }} />
                Your payment information is processed securely. We do not store your payment details.
              </motion.div>
            </form>
          </motion.div>

          {/* Right Column - Order Summary */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            style={summarySectionStyles}
          >
            <div style={sectionHeaderStyles}>
              <h2 style={sectionTitleStyles}>
                <FaShoppingBag style={{ marginRight: '12px' }} /> Order Summary
              </h2>
              <p style={sectionSubtitleStyles}>{cartItems.length} {cartItems.length === 1 ? 'item' : 'items'} in your order</p>
            </div>

            <div style={{ padding: isMobile ? '16px' : '24px', maxHeight: '400px', overflowY: 'auto' }}>
              <AnimatePresence>
                {cartItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                    style={cartItemStyles}
                  >
                    <div style={{ width: '80px', height: '80px', borderRadius: '8px', background: '#f3f4f6', overflow: 'hidden' }}>
                      <img
                        src={item.image || '/placeholder-product.jpg'}
                        alt={item.name}
                        style={itemImageStyles}
                      />
                    </div>
                    <div style={itemDetailsStyles}>
                      <h3 style={itemNameStyles}>{item.name}</h3>
                      <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                        Qty: {item.quantity}
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={itemPriceStyles}>
                          ₹{(parseFloat(item.price || 0) * (1 - parseFloat(item.discount || 0) / 100)).toFixed(2)}
                        </span>
                        {parseFloat(item.discount || 0) > 0 && (
                          <span style={originalPriceStyles}>
                            ₹{parseFloat(item.price || 0).toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              <motion.div
                variants={itemVariants}
                style={{ marginTop: '24px' }}
              >
                <div style={summaryRowStyles}>
                  <span style={summaryLabelStyles}>Subtotal</span>
                  <span style={summaryValueStyles}>₹{calculateTotal()}</span>
                </div>
                <div style={summaryRowStyles}>
                  <span style={summaryLabelStyles}>Shipping</span>
                  <span style={{ ...summaryValueStyles, color: '#10b981' }}>FREE</span>
                </div>
                <div style={summaryRowStyles}>
                  <span style={summaryLabelStyles}>Tax</span>
                  <span style={summaryValueStyles}>Calculated at checkout</span>
                </div>
              </motion.div>

              <motion.div
                variants={itemVariants}
                style={totalRowStyles}
              >
                <span style={totalLabelStyles}>Total</span>
                <span style={totalValueStyles}>₹{calculateTotal()}</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default CheckoutPage;